/**
 * Platform brand marks (OnlyFans / Fansly) rendered as self-contained inline SVG,
 * plus a small corner "flag" badge for overlaying on an avatar.
 *
 * Platform lives on the account (ADR 0019 in bigdayz.new): 'onlyfans' | 'fansly',
 * with null/absent meaning OnlyFans. Keep the null-handling here so callers can
 * pass the raw `account.platform` through.
 */

export type Platform = 'onlyfans' | 'fansly' | null | undefined;

/** Normalise the raw column value to a known platform. */
export function normalizePlatform(platform: Platform): 'onlyfans' | 'fansly' {
  return platform === 'fansly' ? 'fansly' : 'onlyfans';
}

interface LogoProps {
  size?: number;
  className?: string;
  title?: string;
}

/** Fansly brand mark — blue rounded badge with the white heart-and-aperture logo. */
export function FanslyLogo({ size = 16, className = '', title }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={title ?? 'Fansly'}
    >
      {title ? <title>{title}</title> : null}
      <rect width="24" height="24" rx="6" fill="#2699FA" />
      {/* Fansly heart formed by two interlocking ribbons, with the aperture "eye". */}
      <g fill="none" stroke="#fff" strokeWidth="3.1" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20 L5.4 11.2 A4 4 0 1 1 11.7 7.9" />
        <path d="M12 20 L18.6 11.2 A4 4 0 1 0 12.3 7.9" />
      </g>
      <circle cx="12" cy="13" r="2.15" fill="none" stroke="#fff" strokeWidth="1.7" />
      <circle cx="12.8" cy="12.3" r="0.7" fill="#fff" />
    </svg>
  );
}

/** OnlyFans brand mark — teal rounded badge with the ring glyph. */
export function OnlyFansLogo({ size = 16, className = '', title }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={title ?? 'OnlyFans'}
    >
      {title ? <title>{title}</title> : null}
      <rect width="24" height="24" rx="6" fill="#00AFF0" />
      <circle cx="9.5" cy="12" r="3.6" fill="#fff" />
      <circle cx="9.5" cy="12" r="1.5" fill="#00AFF0" />
      <path d="M14.2 8.4h5.2c-.7 2.7-2.6 4-5.7 4l.5-4z" fill="#fff" />
    </svg>
  );
}

/** Pick the right logo for a (possibly null) platform value. */
export function PlatformLogo({ platform, ...rest }: LogoProps & { platform: Platform }) {
  return normalizePlatform(platform) === 'fansly' ? (
    <FanslyLogo {...rest} />
  ) : (
    <OnlyFansLogo {...rest} />
  );
}
